<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SearchResultConversationsList from './SearchResultConversationsList.vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';

import Button from 'next/button/Button.vue';

const emit = defineEmits(['select', 'close']);
const { t } = useI18n();
const store = useStore();
const query = ref('');
const conversations = useMapGetter('conversationSearch/getConversationRecords');
const isFetching = ref(false);
const searchInput = ref(null);
const isInputFocused = ref(false);

const clearSearch = () => {
  store.dispatch('conversationSearch/clearSearchResults');
};

const onSearch = async q => {
  query.value = q;
  clearSearch();
  if (!q) return;
  isFetching.value = true;
  await store.dispatch('conversationSearch/fullSearch', {
    q,
    page: 1,
  });
  isFetching.value = false;
};

const handleSelect = conversation => {
  emit('select', conversation);
};

const debouncedEmit = debounce(
  value => onSearch(value.length > 1 || value.match(/^[0-9]+$/) ? value : ''),
  500
);

const onInput = e => {
  query.value = e.target.value;
  debouncedEmit(query.value);
};

const onFocus = () => {
  isInputFocused.value = true;
};

const onBlur = () => {
  isInputFocused.value = false;
};

onMounted(() => {
  if (searchInput.value) {
    searchInput.value.focus();
  }
});

onUnmounted(() => {
  clearSearch();
});
</script>

<template>
  <div class="flex flex-col gap-4 w-[29rem]">
    <header class="sticky top-0 bg-n-solid-3 z-10">
      <div class="pt-2 ps-4 pe-2 mb-0.5 flex justify-between items-center">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CONVERSATION.DROPDOWN.TITLE'
            )
          }}
        </label>
        <Button
          icon="i-lucide-x"
          size="xs"
          color="slate"
          variant="ghost"
          class="opacity-50 hover:opacity-100"
          @click.prevent="emit('close')"
        />
      </div>
      <div class="mt-2 mx-4 relative">
        <span class="absolute i-lucide-search size-3.5 top-2 left-3" />
        <input
          ref="searchInput"
          v-model="query"
          type="search"
          :placeholder="t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-8 py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-2 text-n-slate-12"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
        />
      </div>
    </header>
    <div class="px-2">
      <SearchResultConversationsList
        :conversations="conversations"
        :query="query"
        :is-fetching="isFetching"
        :show-title="false"
        @select="handleSelect"
      />
    </div>
  </div>
</template>
